/**
 * The Selected card (FD §3.3): what's selected, its numbers, plain-English problems and its settings.
 * With nothing selected it lists every problem in the plan, each clickable.
 */
import { isAnimalArea } from '../../../../core/model';
import { lockArea, setCount, setPlotSmooth, setRiverWidth, setRoadWidth, setSpecies } from '../../../../core/ops';
import type { IconType, Issue, PlanDoc } from '../../../../shared/types';
import { polyArea, polyLen } from '../../../../core/geom';
import { ICONS, KIND_ICON, KIND_LABEL, PLACEABLE_ICONS } from '../../../render/palette';
import { useEditor, type EditorStore, type Selection } from '../../../store/editor';
import { RIVER_W_MAX, RIVER_W_MIN, ROAD_W_MAX, ROAD_W_MIN } from '../../../tools/controller';
import { deleteObject, moveToTray, resizeToNeed } from '../../../tools/edits';
import { NumberField, SpeciesSelect } from '../fields';

const fmt = (n: number) => Math.round(n).toLocaleString('en-US');
const ROAD_KIND: Record<string, string> = { main: 'Main path', cross: 'Cross path', path: 'Path', entrance: 'Entrance path' };

/** The card. */
export function SelectedCard({ store }: { store: EditorStore }) {
  const sel = useEditor(store, (s) => s.selection);
  const doc = useEditor(store, (s) => s.doc);
  const analysis = useEditor(store, (s) => s.analysis);
  const species = useEditor(store, (s) => s.species);
  const derived = useEditor(store, (s) => s.derived);
  const advanced = useEditor(store, (s) => s.advanced);

  const issues = (id: string): Issue[] => (analysis?.issues ?? []).filter((i) => i.targetId === id);
  const chips = (list: Issue[], okText: string) => (
    <div className="chips">
      {list.length ? list.map((i, n) => <span key={n} className={`chip ${i.severity}`}>{i.message}</span>) : analysis ? <span className="chip ok">{okText}</span> : null}
    </div>
  );
  const del = (label = 'Delete') => <button type="button" className="danger-text" onClick={() => deleteObject(store, sel)}>{label}</button>;

  if (!sel) return <Overview store={store} doc={doc} />;

  if (sel.kind === 'plot') {
    const ring = derived.plotRing;
    return (
      <section className="panel">
        <h3>Selected</h3>
        <strong>Zoo boundary</strong>
        <p className="stats">{ring ? `${fmt(polyArea(ring))} m² inside` : ''}</p>
        <label className="check"><input type="checkbox" checked={doc.plot?.smooth ?? true} onChange={(e) => store.apply(setPlotSmooth, { smooth: e.target.checked }, { label: 'Boundary shape' })} /> Smooth corners</label>
        <p className="hint small">Drag the dots to reshape. Double-click the line to add a dot, right-click a dot to remove it.</p>
      </section>
    );
  }

  if (sel.kind === 'area') {
    const a = doc.areas[sel.id];
    if (!a) return null;
    const ring = derived.areaRings[sel.id];
    const area = ring ? polyArea(ring) : 0;
    if (!isAnimalArea(a)) {
      return (
        <section className="panel">
          <h3>Selected</h3>
          <strong>{KIND_LABEL[a.kind]}</strong>
          <p className="stats">{fmt(area)} m²</p>
          <p className="hint small">Drag the dots to reshape and the shape to move it. Double-click the edge to add a dot, right-click a dot to remove it.</p>
          <div className="actions">{del()}</div>
        </section>
      );
    }
    const st = analysis?.habitats[sel.id];
    const sp = species?.find((s) => s.id === a.speciesId);
    return (
      <section className="panel">
        <h3>Selected</h3>
        <strong>{KIND_ICON[a.kind] ? `${KIND_ICON[a.kind]} ` : ''}{sp ? `${sp.name} ${a.kind}` : KIND_LABEL[a.kind]}</strong>
        {species && (
          <label className="field">
            <span>Animal</span>
            <SpeciesSelect species={species} value={a.speciesId} label="Animal" onChange={(id) => {
              const next = species.find((s) => s.id === id);
              store.apply((d: PlanDoc) => {
                setSpecies(d, { areaId: sel.id, speciesId: id });
                const z = d.areas[sel.id];
                if (next && z && isAnimalArea(z) && z.kind !== next.kind) z.kind = next.kind;
              }, undefined, { label: 'Change animal' });
            }} />
          </label>
        )}
        <label className="field">
          <span>Number of animals{sp ? <span className="muted"> (usually {sp.groupSize.min}–{sp.groupSize.max})</span> : null}</span>
          <NumberField value={a.count} min={1} max={500} label="Number of animals" onChange={(v) => store.apply(setCount, { areaId: sel.id, count: v }, { label: 'Number of animals', group: `count:${sel.id}` })} />
        </label>
        {advanced && (
          <label className="check"><input type="checkbox" checked={a.locked} onChange={(e) => store.apply(lockArea, { areaId: sel.id, locked: e.target.checked }, { label: e.target.checked ? 'Lock habitat' : 'Unlock habitat' })} /> Lock (paths and re-arranging leave it alone)</label>
        )}
        <p className="stats">
          {st ? <>{fmt(st.area)} m² of {fmt(st.need)} m² needed{st.needWater > 0 ? <> · water {fmt(st.water)} of {fmt(st.needWater)} m²</> : null}</> : `${fmt(area)} m²`}
        </p>
        {chips(issues(sel.id), 'Looks good')}
        <div className="actions">
          {st && st.need > 0 && Math.abs(st.area - st.need) / st.need > 0.02 && (
            <button type="button" onClick={() => resizeToNeed(store, sel.id)} title="Grow or shrink it around its middle">Resize to needed size</button>
          )}
          <button type="button" onClick={() => moveToTray(store, sel.id)} title="Take it off the map and keep it in Unplaced">Move to Unplaced</button>
          {del()}
        </div>
      </section>
    );
  }

  if (sel.kind === 'road') {
    const r = doc.roads[sel.id];
    if (!r) return null;
    const ws = [...new Set(r.nodes)].map((n) => doc.nodes[n]?.w ?? 0);
    const lo = Math.min(...ws), hi = Math.max(...ws);
    const info = analysis?.roads[sel.id];
    return (
      <section className="panel">
        <h3>Selected</h3>
        <strong>{ROAD_KIND[r.kind] ?? 'Path'}</strong>{r.closed && <span className="tag">loop</span>}
        <label className="field">
          <span>Width (m){lo !== hi ? <span className="muted"> now {fmt1(lo)}–{fmt1(hi)} m</span> : null}</span>
          <NumberField value={round1(hi)} min={ROAD_W_MIN} max={ROAD_W_MAX} step={0.5} label="Width" onChange={(v) => store.apply(setRoadWidth, { roadId: sel.id, w: v }, { label: 'Path width', group: `w:${sel.id}` })} />
        </label>
        <p className="hint small">
          {lo !== hi ? 'Typing a width sets the whole path. ' : ''}
          {advanced ? 'Drag the orange grips at a dot to change the width there.' : 'Switch on Advanced to change the width at single dots.'}
        </p>
        <p className="stats">{info ? `${fmt(info.length)} m long` : ''}</p>
        {info && (info.connected ? <div className="chips"><span className="chip ok">Joined up with the entrance</span></div> : chips(issues(sel.id), ''))}
        <div className="actions">{del()}</div>
      </section>
    );
  }

  if (sel.kind === 'river') {
    const rv = doc.rivers[sel.id];
    if (!rv) return null;
    const p = derived.riverPolys[sel.id];
    return (
      <section className="panel">
        <h3>Selected</h3>
        <strong>River</strong>
        <label className="field">
          <span>Width (m)</span>
          <NumberField value={Math.round(rv.w)} min={RIVER_W_MIN} max={RIVER_W_MAX} label="Width" onChange={(v) => store.apply(setRiverWidth, { riverId: sel.id, w: v }, { label: 'River width', group: `rw:${sel.id}` })} />
        </label>
        <p className="stats">{p ? `${fmt(polyLen(p))} m long` : ''}</p>
        <div className="actions">{del()}</div>
      </section>
    );
  }

  // icon
  const ic = doc.icons[sel.id];
  if (!ic) return null;
  return (
    <section className="panel">
      <h3>Selected</h3>
      {ic.type === 'entrance' ? <strong>{ICONS.entrance.icon} Entrance</strong> : (
        <label className="field">
          <span>Shop or service</span>
          <select value={ic.type} onChange={(e) => {
            const t = e.target.value as IconType;
            store.apply((d: PlanDoc) => { const x = d.icons[sel.id]; if (x) x.type = t; }, undefined, { label: 'Change icon' });
          }}>
            {PLACEABLE_ICONS.map((t) => <option key={t} value={t}>{ICONS[t].icon} {ICONS[t].label}</option>)}
          </select>
        </label>
      )}
      <p className="hint small">Drag it to move it.</p>
      <div className="actions">{del()}</div>
    </section>
  );
}

const round1 = (v: number) => Math.round(v * 10) / 10;
const fmt1 = (v: number) => String(round1(v));

/** Nothing selected: a summary and every problem, clickable. */
function Overview({ store, doc }: { store: EditorStore; doc: PlanDoc }) {
  const analysis = useEditor(store, (s) => s.analysis);
  const speciesError = useEditor(store, (s) => s.speciesError);
  const zones = Object.values(doc.areas).filter(isAnimalArea).length;
  const issues = analysis?.issues ?? [];
  const pick = (i: Issue): Selection => (doc.areas[i.targetId] ? { kind: 'area', id: i.targetId } : doc.roads[i.targetId] ? { kind: 'road', id: i.targetId } : null);
  return (
    <section className="panel">
      <h3>Selected</h3>
      <p className="muted">Click a habitat, path, water or icon on the map.</p>
      {speciesError && <p className="error small">The animal list couldn't be loaded, so sizes aren't checked. {speciesError}</p>}
      <p className="stats">{zones} habitat{zones === 1 ? '' : 's'} · {Object.keys(doc.roads).length} path{Object.keys(doc.roads).length === 1 ? '' : 's'}</p>
      {analysis && (issues.length ? (
        <>
          <strong className="small">Things to look at ({issues.length})</strong>
          <ul className="issues-list">
            {issues.map((i, n) => (
              <li key={n}><button type="button" className={i.severity} onClick={() => store.select(pick(i))}>{i.severity === 'bad' ? '⛔' : '⚠️'} {i.message}</button></li>
            ))}
          </ul>
        </>
      ) : zones ? <div className="chips"><span className="chip ok">Every habitat looks good</span></div> : null)}
    </section>
  );
}
